
import { ActivityType } from '@/types';

export interface UnifiedRewards {
  points: number;
  xp: number;
  gold: number;
  healthBonus: number;
  hasExtras: boolean;
}

/**
 * Calculate unified rewards for both direct activities and habit completions
 * Direct activities get ONLY points
 * Habit-based activities get points + XP + Gold + extras
 */
export function calculateUnifiedRewards(
  activityOrHabit: any,
  isHabitBased: boolean = false,
  habiticaCalculation?: any
): UnifiedRewards {
  // Get base points from original activity or habit
  const basePoints = activityOrHabit.basePoints || activityOrHabit.points || 1;
  
  if (!isHabitBased) {
    // Direct activity logging: POINTS ONLY
    return {
      points: basePoints,
      xp: 0,
      gold: 0,
      healthBonus: 0,
      hasExtras: false
    };
  }
  
  // Habit-based completion: FULL REWARDS
  return {
    points: basePoints, // Preserve original activity points
    xp: habiticaCalculation?.experience || basePoints * 2, // Habitica XP calculation
    gold: habiticaCalculation?.gold || Math.floor(basePoints / 2), // Habitica Gold calculation
    healthBonus: basePoints >= 4 ? 5 : 2, // Health bonus for habit completion
    hasExtras: true
  };
}

/**
 * Get accountability message based on activity type
 */
export function getAccountabilityMessage(activity: ActivityType): string {
  const potentialXP = activity.points * 2;
  const potentialGold = Math.floor(activity.points / 2);
  
  return `💡 Convert "${activity.name}" to a habit/daily to earn +${potentialXP} XP, +${potentialGold} gold, and pet drops!`;
}

/**
 * Check if an activity should show conversion suggestion
 */
export function shouldSuggestConversion(activityName: string, activityCount: number): boolean {
  // Suggest conversion after 3 completions of the same activity
  return activityCount >= 3;
}

/**
 * Calculate missed opportunities (XP/Gold that could have been earned)
 */
export function calculateMissedOpportunities(activities: any[]): { totalXP: number; totalGold: number } {
  let totalXP = 0;
  let totalGold = 0;
  
  activities.forEach(activity => {
    if (!activity.isHabitBased) {
      totalXP += activity.points * 2;
      totalGold += Math.floor(activity.points / 2);
    }
  });
  
  return { totalXP, totalGold };
}
